import { useMemo } from 'react';
import { ROOTS, paths } from 'src/routes/paths';
import { useLocales } from 'src/locales';
import SvgColor from 'src/components/svg-color';
import { useGetWorkspacesQuery } from 'src/context/api/workspaces';
import { useAuth } from 'src/auth';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import Iconify from 'src/components/iconify';
import { useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';

const icon = (name: string) => (
  <SvgColor src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />
);

export function useNavData() {
  const { t } = useLocales();
  const workspaces = useGetWorkspacesQuery(null);
  const { user } = useAuth();
  const workspace = useCurrentWorkspace();
  const demoMode = useSelector((state: any) => state.filters.demoMode);
  const role = user?.role;
  const isLead = workspace?.template && workspace.template.trim().toLowerCase() === 'lead';
  const theme = useTheme();
  const filterStore = useSelector((state: any) => state.filters);
  const isMonoCountry = filterStore?.loadedCountries?.length === 1;

  const ICONS = {
    brandOverview: icon('solar--chart-2-bold-duotone-light-mode'),
    optimizationHub: icon('solar--pie-chart-2-bold-duotone-light-mode'),
    competitiveIntelligence: icon('solar--ranking-bold-duotone-light-mode'),
    adminUsers: icon('solar--users-group-rounded-bold-duotone-light-mode'),
    sso: <Iconify icon="material-symbols:add-moderator-rounded" />,
    workspaces: icon('solar--widget-bold-duotone-light-mode'),
    brandPulse: icon('ic_competitors'),
  };

  const data = useMemo(() => {
    const dashboardSection = {
      subheader: 'Brand',
      items: [
        {
          title: 'Brand Overview',
          path: paths.dash.brandOverview,
          icon: ICONS.brandOverview,
        },
        {
          title: 'Optimization Hub',
          path: paths.dash.overview,
          icon: ICONS.optimizationHub,
          activePaths: [
            paths.dash.overview,
            paths.dash.conversionsAnalysis,
            paths.dash.optimizationActivationsOverview,
            paths.dash.trafficAnalysis,
            paths.dash.campaignExplorer,
            paths.dash.trafficDetails,
          ],
          children: [
            {
              title: 'Performance',
              path: paths.dash.overview,
              activePaths: [
                paths.dash.overview,
                paths.dash.conversionsAnalysis,
                paths.dash.trafficAnalysis,
                paths.dash.trafficDetails,
              ],
            },
            {
              title: 'Campaigns',
              path: paths.dash.campaignExplorer,
              lastChildren: demoMode || !['admin', 'brand specialist'].includes(role),
            },
            ...(
              !demoMode && ['admin', 'brand specialist'].includes(role)
                ? [{
                  title: 'Activations',
                  path: paths.dash.optimizationActivationsOverview,
                  lastChildren: true,
                  activePaths: [
                    paths.dash.activeMode,
                    paths.dash.koBiddingMaxCPC,
                    paths.dash.koBiddingTROAS,
                    paths.dash.koBiddingTCPA,
                    paths.dash.koBiddingTargetImpressionShare,
                    paths.dash.koBiddingAuto,
                    paths.dash.scraping,
                    paths.dash.optimizationActivationsOverview
                  ],
                }]
                : []
            ),
          ],
        },

        ...(
          role !== 'customer'
            ? [{
              title: '[NEW] Competitive Intelligence',
              path: paths.dash.competitiveOverview,
              icon: ICONS.competitiveIntelligence,
              activePaths: [
                paths.dash.competitiveOverview,
                paths.dash.competitionEvolution,
                paths.dash.competitiveSearchTermAnalysis,
                paths.dash.competitiveAds,
              ],
            }]
            : []
        ),

        {
          title: t('nav.competitiveIntelligence'),
          path: paths.dash.competitorsMonitoring,
          icon: ICONS.competitiveIntelligence,
          children: [
            {
              title: t('nav.competitorsMonitoring'),
              path: paths.dash.competitorsMonitoring,
            },
            {
              title: t('nav.competitorFocus'),
              path: paths.dash.competitorFocus,
            },
            {
              title: t('nav.textAds'),
              path: paths.dash.textAds,
              lastChildren: isLead,
            },
            ...((!isLead || workspace?.shoppingIncluded === true)
              ? [{
                title: t('nav.productListingAds'),
                path: paths.dash.productListingAds,
                lastChildren: true,
              }]
              : []),
          ],
        },
      ],
    };

    const adminSection =
      !demoMode && ['admin', 'brand specialist', 'consultant'].includes(role)
        ? [{
          subheader: 'Administration',
          items: [
            {
              title: t('nav.users'),
              path: ROOTS.ADMIN_USERS,
              icon: ICONS.adminUsers,
              children: [
                {
                  title: t('nav.list'),
                  path: paths.admin.user,
                },
                {
                  title: t('nav.add_User'),
                  path: paths.admin.userAdd,
                  lastChildren: true,
                },
              ],
            },
            {
              title: 'Workspaces',
              path: '#',
              icon: ICONS.workspaces,
              children: [
                {
                  title: t('nav.list'),
                  path: paths.admin.workspaces,
                },
                ...(role === 'admin'
                  ? [{
                    title: t('dashboard.newWorkspace'),
                    path: paths.admin.workspacesAdd,
                    lastChildren: true,
                  }]
                  : []),
              ],
            },
            ...(role === 'admin'
              ? [{
                title: 'SSO Integrations',
                path: paths.admin.sso.root,
                icon: ICONS.sso,
                children: [{
                  title: 'List',
                  path: paths.admin.sso.list,
                  lastChildren: true,
                }],
              }]
              : []),
          ],
        }]
        : [];


    const brandPulseSection =
      workspace?.brandpulse && (workspace?.brandpulse_visibility?.includes(role))
        ? [{
          subheader: 'Brand Pulse',
          items: [
            {
              title: 'My Competitors',
              path: paths.dash.brandPulse,
              icon: ICONS.brandPulse,
            }
          ],
        }]
        : [];

    const nonBrandSection =
      workspace?.nonbrand && (workspace?.nonbrand_visibility?.includes(role))
        ? [{
          subheader: 'Non Brand',
          items: [
            {
              title: 'Search terms',
              path: paths.dash.nonBrandOverview,
              icon: ICONS.optimizationHub,
            },
            {
              title: 'Competitors',
              path: paths.dash.nonBrandCompetitors,
              icon: ICONS.competitiveIntelligence,
            },
          ],
        }]
        : [];


    return [dashboardSection, ...nonBrandSection, ...brandPulseSection, ...adminSection];
  }, [
    t,
    workspaces.data,
    role,
    workspace?.template,
    isLead,
    demoMode,
    theme.palette.mode,
    isMonoCountry,
    workspace
  ]);

  return data;
}
